import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { api, isSpendApprovalError } from '../api';
import { errorMessage } from '../errors';
import { ROLE_LABELS } from './roles';
import { BULK_INVITE_MAX, parseInviteList } from './parseInviteList';

const GHL_STATUS = {
  member: 'Already in this workspace',
  invited: 'Invited',
  no_email: 'No email in GoHighLevel',
};
// Reasons worth telling the admin; `not_ghl_tenant` means this workspace has no GHL at all.
const GHL_NOTE = {
  missing_scope: 'The GoHighLevel connection cannot read users, so paste emails instead.',
  ghl_unavailable: 'GoHighLevel did not answer, so paste emails instead.',
  not_connected: 'GoHighLevel is not connected, so paste emails instead.',
};

// Ticket 202: invite several people at once. Each row is its own POST /invitations,
// so every server check (spend approval, seat rules, rate limit, audit) runs per row
// exactly as for a single invite. `roles` is the list the caller may hand out.
// Ticket 230: the GHL workspace can also pick people from its GoHighLevel user list.
export default function BulkInviteDialog({ open, onOpenChange, roles, onDone }) {
  const [text, setText] = useState('');
  const [role, setRole] = useState('loan_officer');
  const [results, setResults] = useState(null); // { [email]: { state, detail } }
  const [busy, setBusy] = useState(false);
  const [ghl, setGhl] = useState(null); // GET /invitations/ghl-users body
  const [tab, setTab] = useState('paste');
  const tabTouched = useRef(false); // the GHL list arriving late never yanks an admin off the paste tab
  const [picked, setPicked] = useState({}); // { [email]: role }
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    api.get('/invitations/ghl-users')
      .then(({ data }) => { if (!live) return; setGhl(data); if (data.available && !tabTouched.current) setTab('ghl'); })
      .catch(() => { if (live) setGhl(null); });
    return () => { live = false; };
  }, [open]);

  const rows = useMemo(
    () => parseInviteList(text, { defaultRole: role, allowedRoles: roles }),
    [text, role, roles],
  );
  const ghlUsers = ghl?.available ? ghl.users : [];
  const good = tab === 'ghl'
    ? ghlUsers.filter((u) => u.email && picked[u.email]).map((u) => ({ email: u.email, role: picked[u.email], name: u.name }))
    : rows.filter((r) => !r.error);
  const over = good.length > BULK_INVITE_MAX;
  const sent = results ? Object.values(results).filter((r) => r.state === 'sent').length : 0;

  // GHL picks start as a loan officer, never as the paste tab's hidden default role.
  const ghlRole = roles.includes('loan_officer') ? 'loan_officer' : roles[0];
  const pickable = (u) => u.email && u.status !== 'member' && u.status !== 'no_email';
  const toggle = (email) => setPicked((p) => {
    const next = { ...p };
    if (next[email]) delete next[email]; else next[email] = ghlRole;
    return next;
  });
  const selectNew = () => setPicked((p) => {
    const next = { ...p };
    ghlUsers.filter((u) => u.status === 'not_invited' && !next[u.email]).forEach((u) => { next[u.email] = ghlRole; });
    return next;
  });

  const close = (next) => {
    if (busy) return;
    onOpenChange(next);
    if (!next) { setText(''); setResults(null); setPicked({}); setGhl(null); setTab('paste'); tabTouched.current = false; if (sent) onDone?.(); }
  };

  const loadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setText(await file.text());
  };

  const send = async () => {
    const ok = await confirmDialog({
      title: `Email ${good.length} invitation${good.length === 1 ? '' : 's'}?`,
      description: 'Each person gets an email with a personal link. Sending the same address again replaces its earlier link.',
      confirmText: 'Send invitations',
    });
    if (!ok) return;
    setBusy(true);
    const out = {};
    setResults(out);
    let stopped = null;
    for (const r of good) {
      if (stopped) { out[r.email] = { state: 'skipped', detail: stopped }; continue; }
      try {
        const { data } = await api.post('/invitations', { email: r.email, role: r.role, ...(r.name ? { name: r.name } : {}) });
        out[r.email] = data.email_sent
          ? { state: 'sent', detail: 'Emailed' }
          : { state: 'sent', detail: 'Created, but the email could not be sent' };
      } catch (err) {
        if (isSpendApprovalError(err)) stopped = 'Not sent: spending is not approved for this workspace';
        else if (err?.response?.status === 429) stopped = 'Not sent: hourly invitation limit reached, try again later';
        out[r.email] = { state: 'failed', detail: errorMessage(err) };
      }
      setResults({ ...out });
    }
    setResults({ ...out });
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
        <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Invite several people</DialogTitle></DialogHeader>
        {!results ? (
          <div className="space-y-3">
            {ghl?.available && (
              <div className="flex gap-1 border-b border-slate-100" role="tablist">
                {[['ghl', 'From GoHighLevel'], ['paste', 'Paste emails']].map(([key, label]) => (
                  <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => { tabTouched.current = true; setTab(key); }}
                    className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${tab === key ? 'border-[#070B5F] text-[#070B5F] font-medium' : 'border-transparent text-slate-500'}`}
                    data-testid={`bulk-invite-tab-${key}`}>{label}</button>
                ))}
              </div>
            )}
            {!ghl?.available && GHL_NOTE[ghl?.reason] && (
              <p className="text-xs text-slate-500" data-testid="bulk-invite-ghl-note">{GHL_NOTE[ghl.reason]}</p>
            )}
            {tab === 'ghl' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Your GoHighLevel users</Label>
                  <button type="button" onClick={selectNew} className="text-xs text-[#070B5F] underline" data-testid="bulk-invite-ghl-select-new">
                    Select everyone not invited
                  </button>
                </div>
                <ul className="max-h-64 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-100 text-sm" data-testid="bulk-invite-ghl-list">
                  {ghlUsers.map((u, i) => {
                    const key = u.email || `no-email-${i}`;
                    const on = Boolean(u.email && picked[u.email]);
                    return (
                      <li key={key} className="flex items-center gap-3 px-3 py-1.5" data-testid={`bulk-invite-ghl-row-${key}`}>
                        <input type="checkbox" checked={on} disabled={!pickable(u)} onChange={() => toggle(u.email)}
                          aria-label={`Invite ${u.name || u.email}`} data-testid={`bulk-invite-ghl-check-${key}`} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{u.name || u.email || 'Unnamed user'}{u.ghl_role && <span className="ml-2 text-xs text-slate-400">GHL {u.ghl_role}</span>}</div>
                          {u.email && <div className="truncate text-xs text-slate-500">{u.email}</div>}
                        </div>
                        {on ? (
                          <Select value={picked[u.email]} onValueChange={(v) => setPicked((p) => ({ ...p, [u.email]: v }))}>
                            <SelectTrigger className="h-8 w-40 shrink-0" data-testid={`bulk-invite-ghl-role-${key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : GHL_STATUS[u.status] && <span className="text-xs text-slate-500 shrink-0">{GHL_STATUS[u.status]}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <>
              <div>
                <Label>Emails</Label>
                <textarea value={text} onChange={(e) => { tabTouched.current = true; setText(e.target.value); }} rows={6}
                  placeholder={'ann@example.com\nbob@example.com, Processor'}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono" data-testid="bulk-invite-text" />
                <p className="text-xs text-slate-500 mt-1">One per line, or comma separated. Add a role after the email (for example <span className="font-mono">bob@example.com, Processor</span>) to override the default. Up to {BULK_INVITE_MAX} at a time.</p>
                <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" onChange={loadFile} className="hidden" data-testid="bulk-invite-file" />
                <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline mt-2" data-testid="bulk-invite-upload">
                  <Upload size={14} /> Load a CSV
                </button>
              </div>
              <div>
                <Label>Default role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="mt-1" data-testid="bulk-invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              </>
            )}
            {rows.length > 0 && tab === 'paste' && (
              <ul className="max-h-48 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-100 text-sm" data-testid="bulk-invite-preview">
                {rows.map((r) => (
                  <li key={r.email} className="flex items-center justify-between gap-3 px-3 py-1.5">
                    <span className="truncate">{r.email}</span>
                    {r.error
                      ? <span className="text-xs text-red-600 shrink-0" data-testid={`bulk-invite-bad-${r.email}`}>{r.error}</span>
                      : <span className="text-xs text-slate-500 shrink-0">{ROLE_LABELS[r.role]}</span>}
                  </li>
                ))}
              </ul>
            )}
            {over && <p className="text-xs text-red-600" data-testid="bulk-invite-over">That is {good.length} people. Send {BULK_INVITE_MAX} or fewer, then the rest in an hour.</p>}
            <div className="flex justify-end">
              <button type="button" onClick={send} disabled={!good.length || over || busy} className="btn-gold" data-testid="bulk-invite-send">
                Send {good.length || ''} invitation{good.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <ul className="max-h-72 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-100 text-sm" data-testid="bulk-invite-results">
              {good.map((r) => {
                const res = results[r.email];
                return (
                  <li key={r.email} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`bulk-invite-result-${r.email}`}>
                    <span className="truncate">{r.email}</span>
                    {!res ? <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />
                      : <span className={`text-xs shrink-0 ${res.state === 'sent' ? 'text-emerald-700' : 'text-red-600'}`}>{res.detail}</span>}
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500" data-testid="bulk-invite-summary">{busy ? 'Sending…' : `${sent} of ${good.length} sent`}</span>
              <button type="button" onClick={() => close(false)} disabled={busy} className="btn-outline" data-testid="bulk-invite-done">Done</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
